import type { QuoteOut } from '../types';

const readText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized || null;
};

export const getQuoteCustomerName = (snapshot?: Record<string, unknown> | null): string | null => {
  const data = snapshot ?? {};
  const firstName = readText(data.first_name);
  const lastName = readText(data.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  return fullName || readText(data.name) || readText(data.full_name) || readText(data.display_name);
};

export const formatQuoteCustomerLabel = (quote: Pick<QuoteOut, 'customer_id' | 'customer_snapshot'>): string => {
  const snapshot = quote.customer_snapshot ?? {};
  const name = getQuoteCustomerName(snapshot);
  const email = readText(snapshot.email);
  const phone = readText(snapshot.phone) ?? readText(snapshot.tel);

  if (quote.customer_id != null) {
    return `[${quote.customer_id}] ${name || 'Cliente sin nombre'}`;
  }
  return name || email || phone || 'Sin cliente/contacto';
};

export const buildPublicQuoteUrl = (origin: string, publicId: string, token: string): string =>
  `${origin.replace(/\/$/, '')}/q/${encodeURIComponent(publicId)}?token=${encodeURIComponent(token)}`;

export type ClipboardCopyResult = 'copied' | 'manual';

export const copyTextToClipboard = async (value: string): Promise<ClipboardCopyResult> => {
  const isTrustedContext = typeof window === 'undefined' || window.isSecureContext;
  if (!isTrustedContext || !navigator.clipboard?.writeText) {
    return 'manual';
  }

  try {
    await navigator.clipboard.writeText(value);
    if (navigator.clipboard.readText) {
      try {
        const clipboardValue = await navigator.clipboard.readText();
        return clipboardValue === value ? 'copied' : 'manual';
      } catch {
        // Some browsers allow writeText but deny readText; keep the successful write.
      }
    }
    return 'copied';
  } catch {
    return 'manual';
  }
};

/*
 * Clipboard access is intentionally not emulated with execCommand here. On
 * HTTP/IP origins Chromium can report success while leaving the system
 * clipboard untouched. The caller must provide a manual-copy fallback.
 */
export const selectTextForManualCopy = (element: HTMLTextAreaElement): void => {
  element.focus();
  element.select();
  element.setSelectionRange(0, element.value.length);
};
